import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, collectionGroup, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, query, where } from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

export const PORTAL = {
    OWNER: 'owner',
    TENANT: 'tenant',
};

function getDb() {
    return getFirestore(getFirebaseApp());
}

async function loadProfiles(uid) {
    const db = getDb();
    const ownerSnap = await getDoc(doc(db, COLLECTIONS.OWNERS, uid));

    // If the user is an owner, don't query staff at all.
    // This avoids "permission-denied" when staff rules aren't deployed yet
    // and also keeps owner login fast.
    if (ownerSnap.exists()) {
        return { ownerSnap, staffDoc: null, tenantId: null, tenantSnap: null, studentSnap: null };
    }

    const lookupSnap = await getDoc(doc(db, COLLECTIONS.STAFF_LOOKUP, uid));
    let tenantId = lookupSnap.exists() ? lookupSnap.data()?.tenantId : null;

    // Another fallback: some deployments store the tenant link in tenant_users/{uid}.
    // This keeps login working even if staff_lookup hasn't been created yet.
    if (!tenantId) {
        const tenantUserSnap = await getDoc(doc(db, COLLECTIONS.TENANT_USERS, uid));
        tenantId = tenantUserSnap.exists() ? tenantUserSnap.data()?.tenantId : null;
    }

    // Fallback for older data: allow tenant login for the primary admin by looking up
    // tenants where adminUid == uid (single-field query, no composite index needed).
    if (!tenantId) {
        const tQ = query(collection(db, COLLECTIONS.TENANTS), where('adminUid', '==', uid), limit(1));
        const tSnap = await getDocs(tQ);
        const tDoc = tSnap.docs[0] || null;
        tenantId = tDoc?.id || null;
    }

    // Student Lookup check
    let studentSnap = null;
    let studentTenantId = null;
    if (!tenantId) {
        try {
            const studentLookupSnap = await getDoc(doc(db, 'student_lookup', uid));
            if (studentLookupSnap.exists()) {
                studentTenantId = studentLookupSnap.data()?.tenantId;
            }
            if (!studentTenantId) {
                // query group collection 'students' where authUid == uid
                const studentQ = query(collectionGroup(db, 'students'), where('authUid', '==', uid), limit(1));
                const studentDocsSnap = await getDocs(studentQ);
                if (!studentDocsSnap.empty) {
                    const sDoc = studentDocsSnap.docs[0];
                    const parts = sDoc.ref.path.split('/');
                    if (parts[0] === 'Students' && parts[1]) {
                        studentTenantId = parts[1];
                    } else if (sDoc.data()?.tenantId) {
                        studentTenantId = sDoc.data()?.tenantId;
                    }
                }
            }
            if (studentTenantId) {
                const sSnap = await getDoc(doc(db, 'Students', studentTenantId, 'students', uid));
                if (sSnap.exists()) {
                    studentSnap = sSnap;
                    tenantId = studentTenantId;
                }
            }
        } catch (err) {
            console.error('Error loading student profile in sessionService:', err);
        }
    }

    if (!tenantId) {
        return { ownerSnap, staffDoc: null, tenantId: null, tenantSnap: null, studentSnap: null };
    }

    const staffDocSnap = await getDoc(doc(db, COLLECTIONS.STAFF, tenantId, COLLECTIONS.STAFF, uid));
    const staffDoc = staffDocSnap.exists() ? staffDocSnap : null;

    // For tenant admins, a staff doc might not exist yet. Fetch tenant doc so
    // we can still authorize and build a session for adminUid users.
    let tenantSnap = null;
    try {
        const tSnap = await getDoc(doc(db, COLLECTIONS.TENANTS, tenantId));
        tenantSnap = tSnap.exists() ? tSnap : null;
    } catch {
        tenantSnap = null;
    }

    return { ownerSnap, staffDoc, tenantId, tenantSnap, studentSnap };
}

async function buildTenantUserFromStaffDoc(fbUser, staffDoc) {
    const staffData = staffDoc?.data?.() || {};
    const tenantId = staffDoc?.ref?.parent?.parent?.id || staffData.tenantId || null;

    let tenantData = null;
    if (tenantId) {
        const tSnap = await getDoc(doc(getDb(), COLLECTIONS.TENANTS, tenantId));
        tenantData = tSnap.exists() ? tSnap.data() : null;
    }

    const name =
        staffData?.displayName ||
        tenantData?.schoolName ||
        fbUser.displayName ||
        (fbUser.email ? fbUser.email.split('@')[0] : 'User');

    const access = staffData?.authorities?.access || {};
    const permissionFromAccess = (resourceKey) => {
        const v = access?.[resourceKey];
        if (v === 'view') return `${resourceKey}.view`;
        if (v === 'full') return `${resourceKey}.full`;
        if (v === 'admin') return `${resourceKey}.full`;
        return null;
    };
    const derived = [
        permissionFromAccess('dashboard'),
        permissionFromAccess('students'),
        permissionFromAccess('teachers'),
        permissionFromAccess('staff'),
        permissionFromAccess('tenantProfile'),
        permissionFromAccess('classes'),
        permissionFromAccess('schoolTime'),
        permissionFromAccess('preferences'),
        permissionFromAccess('studentAttendance'),
        permissionFromAccess('teacherAttendance'),
        permissionFromAccess('staffAttendance'),
        permissionFromAccess('financeAccounts'),
        permissionFromAccess('receiveLedger'),
        permissionFromAccess('expenseLedger'),
        permissionFromAccess('transactionLedger'),
        permissionFromAccess('feesManagement'),
        permissionFromAccess('teacherReport'),
        permissionFromAccess('studentReport'),
        permissionFromAccess('staffReport'),
    ].filter(Boolean);

    return {
        uid: fbUser.uid,
        email: fbUser.email,
        name,
        role: PORTAL.TENANT,
        tenantId,
        schoolName: tenantData?.schoolName ?? null,
        region: tenantData?.region ?? null,
        plan: tenantData?.plan ?? null,
        logoUrl: tenantData?.logoUrl ?? null,
        logoPublicId: tenantData?.logoPublicId ?? null,
        attendanceConfig: tenantData?.attendanceConfig ?? null,
        preferences: tenantData?.preferences ?? null,
        staffRole: staffData?.role ?? null,
        // Store explicit permissions (e.g. students.full) rather than a wildcard,
        // so routing/ability logic stays predictable.
        permissions: Array.isArray(staffData?.permissions)
            ? staffData.permissions
            : derived,
        adminRoles: [],
    };
}

function buildOwnerUser(fbUser, ownerData) {
    const name =
        ownerData?.displayName ||
        fbUser.displayName ||
        (fbUser.email ? fbUser.email.split('@')[0] : 'User');

    return {
        uid: fbUser.uid,
        email: fbUser.email,
        name,
        role: PORTAL.OWNER,
        permissions:
            ['dashboard.view', 'tenants.manage', 'announcements.manage'],
        adminRoles: ['owner', 'admin'],
        tenantId: null,
        schoolName: null,
    };
}

/**
 * Sign in and enforce owner vs school portal using Firestore `owners` / `tenant_users`.
 */
export async function signInWithPortal(email, password, expectedPortal) {
    const auth = getAuth(getFirebaseApp());
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const { ownerSnap, staffDoc, tenantId, tenantSnap, studentSnap } = await loadProfiles(cred.user.uid);

    const hasOwner = ownerSnap.exists();
    const isTenantAdmin = !!tenantSnap && tenantSnap.data()?.adminUid === cred.user.uid;
    const hasTenant = !!staffDoc || isTenantAdmin;
    const hasStudent = !!studentSnap;

    if (expectedPortal === PORTAL.OWNER && !hasOwner) {
        await signOut(auth);
        throw new Error('This account is not registered as a platform owner.');
    }
    if (expectedPortal === PORTAL.TENANT && !hasTenant && !hasStudent) {
        await signOut(auth);
        throw new Error('This account is not linked to a school (tenant).');
    }

    if (expectedPortal === PORTAL.TENANT && hasTenant) {
        const tenantData = tenantSnap?.data?.();
        if (tenantData && (tenantData.status === 'Inactive' || tenantData.status === 'inactive')) {
            await signOut(auth);
            const reason = tenantData.inactiveReason || tenantData.deactivationReason || 'No reason specified';
            throw new Error(`Your account is inactive. Reason: ${reason}. Please contact EDULINK support.`);
        }
    }

    if (expectedPortal === PORTAL.TENANT && hasStudent) {
        const studentData = studentSnap.data() || {};
        if (studentData.status === 'Inactive' || studentData.status === 'inactive') {
            await signOut(auth);
            throw new Error(`Your student account is inactive. Please contact school administration.`);
        }
    }

    if (expectedPortal === PORTAL.OWNER && hasOwner) {
        return {
            user: buildOwnerUser(cred.user, ownerSnap.data()),
            firebaseUser: cred.user,
        };
    }
    if (expectedPortal === PORTAL.TENANT && hasTenant) {
        return {
            user: staffDoc
                ? await buildTenantUserFromStaffDoc(cred.user, staffDoc)
                : {
                    uid: cred.user.uid,
                    email: cred.user.email,
                    name:
                        tenantSnap?.data?.()?.schoolName ||
                        cred.user.displayName ||
                        (cred.user.email ? cred.user.email.split('@')[0] : 'User'),
                    role: PORTAL.TENANT,
                    tenantId: tenantId || tenantSnap?.id || null,
                    schoolName: tenantSnap?.data?.()?.schoolName ?? null,
                    region: tenantSnap?.data?.()?.region ?? null,
                    plan: tenantSnap?.data?.()?.plan ?? null,
                    logoUrl: tenantSnap?.data?.()?.logoUrl ?? null,
                    logoPublicId: tenantSnap?.data?.()?.logoPublicId ?? null,
                    attendanceConfig: tenantSnap?.data?.()?.attendanceConfig ?? null,
                    preferences: tenantSnap?.data?.()?.preferences ?? null,
                    staffRole: 'admin',
                    permissions: ['*'],
                    adminRoles: [],
                },
            firebaseUser: cred.user,
        };
    }
    if (expectedPortal === PORTAL.TENANT && hasStudent) {
        const studentData = studentSnap.data() || {};
        const tenantData = tenantSnap?.exists() ? tenantSnap.data() : {};
        return {
            user: {
                uid: cred.user.uid,
                email: cred.user.email,
                name: studentData.firstName ? `${studentData.firstName} ${studentData.lastName || ''}`.trim() : studentData.name || 'Student',
                role: 'student',
                tenantId: tenantId || tenantSnap?.id || null,
                schoolName: tenantData?.schoolName ?? null,
                region: tenantData?.region ?? null,
                plan: tenantData?.plan ?? null,
                logoUrl: tenantData?.logoUrl ?? null,
                logoPublicId: tenantData?.logoPublicId ?? null,
                studentData,
                permissions: ['student.dashboard', 'student.profile', 'student.attendance', 'student.homeworks', 'student.timetable', 'student.fees'],
            },
            firebaseUser: cred.user,
        };
    }

    await signOut(auth);
    throw new Error('Unable to determine account access.');
}

/**
 * Map Firebase user to app user after refresh. Prefer owner if both docs exist.
 */
export async function syncSessionFromFirebaseUser(fbUser) {
    if (!fbUser) return null;
    const { ownerSnap, staffDoc, tenantId, tenantSnap, studentSnap } = await loadProfiles(fbUser.uid);

    if (ownerSnap.exists()) {
        return {
            user: buildOwnerUser(fbUser, ownerSnap.data()),
            firebaseUser: fbUser,
        };
    }
    if (staffDoc) {
        if (tenantSnap?.exists()) {
            const tenantData = tenantSnap.data();
            if (tenantData.status === 'Inactive' || tenantData.status === 'inactive') {
                return null;
            }
        }
        return {
            user: await buildTenantUserFromStaffDoc(fbUser, staffDoc),
            firebaseUser: fbUser,
        };
    }
    if (studentSnap) {
        const studentData = studentSnap.data() || {};
        if (studentData.status === 'Inactive' || studentData.status === 'inactive') {
            return null;
        }
        const tenantData = tenantSnap?.exists() ? tenantSnap.data() : {};
        return {
            user: {
                uid: fbUser.uid,
                email: fbUser.email,
                name: studentData.firstName ? `${studentData.firstName} ${studentData.lastName || ''}`.trim() : studentData.name || 'Student',
                role: 'student',
                tenantId: tenantId || tenantSnap?.id || null,
                schoolName: tenantData?.schoolName ?? null,
                region: tenantData?.region ?? null,
                plan: tenantData?.plan ?? null,
                logoUrl: tenantData?.logoUrl ?? null,
                logoPublicId: tenantData?.logoPublicId ?? null,
                studentData,
                permissions: ['student.dashboard', 'student.profile', 'student.attendance', 'student.homeworks', 'student.timetable', 'student.fees'],
            },
            firebaseUser: fbUser,
        };
    }
    // Allow tenant admins to persist a tenant session even if staff doc isn't created yet.
    if (tenantSnap?.exists?.() && tenantSnap.data()?.adminUid === fbUser.uid) {
        const tData = tenantSnap.data() || {};
        if (tData.status === 'Inactive' || tData.status === 'inactive') {
            return null;
        }
        return {
            user: {
                uid: fbUser.uid,
                email: fbUser.email,
                name:
                    tData?.schoolName ||
                    fbUser.displayName ||
                    (fbUser.email ? fbUser.email.split('@')[0] : 'User'),
                role: PORTAL.TENANT,
                tenantId: tenantId || tenantSnap.id || null,
                schoolName: tData?.schoolName ?? null,
                region: tData?.region ?? null,
                plan: tData?.plan ?? null,
                logoUrl: tData?.logoUrl ?? null,
                logoPublicId: tData?.logoPublicId ?? null,
                attendanceConfig: tData?.attendanceConfig ?? null,
                preferences: tData?.preferences ?? null,
                staffRole: 'admin',
                permissions: ['*'],
                adminRoles: [],
            },
            firebaseUser: fbUser,
        };
    }
    return null;
}

export async function firebaseSignOut() {
    await signOut(getAuth(getFirebaseApp()));
}

export function subscribeAuthState(callback) {
    return onAuthStateChanged(getAuth(getFirebaseApp()), callback);
}

/**
 * Live staff doc updates (used to live-refresh permissions/menu without logout)
 */
export function subscribeStaffDoc({ tenantId, staffId, onNext, onError }) {
    if (!tenantId || !staffId) throw new Error('Missing tenantId/staffId');
    const ref = doc(getDb(), COLLECTIONS.STAFF, tenantId, COLLECTIONS.STAFF, staffId);
    return onSnapshot(
        ref,
        (snap) => onNext?.(snap.exists() ? snap.data() : null),
        (err) => onError?.(err)
    );
}

export async function getIdTokenOrNull(fbUser) {
    if (!fbUser?.getIdToken) return null;
    try {
        return await fbUser.getIdToken();
    } catch {
        return null;
    }
}
